import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    try {
      const params = new URLSearchParams();
      params.append("email", email);
      const res = await fetch("https://readdy.ai/api/form/d824ekl979rtuqs72ge0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const navGroups = [
    {
      title: "初めての方へ",
      links: ["レディースキッドとは", "フィッティングの流れ", "足タイプ診断", "よくある質問"],
    },
    {
      title: "商品を探す",
      links: ["パンプス", "ローファー", "ブーツ・パンプス", "インソール"],
    },
    {
      title: "コンテンツ",
      links: ["ブログ・読みもの", "お客様の声", "スタッフコラム", "YouTube"],
    },
  ];

  return (
    <footer className="bg-[#1C1917] text-white">
      {/* Brand philosophy */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left image */}
            <div className="rounded-2xl overflow-hidden w-full h-64 md:h-72">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20minimalist%20women%20shoe%20boutique%20interior%20at%20golden%20hour%2C%20warm%20wooden%20display%20shelves%20with%20curated%20neutral%20pumps%2C%20soft%20ambient%20lighting%2C%20inviting%20and%20sophisticated%20retail%20atmosphere%2C%20cream%20and%20warm%20brown%20tones%2C%20no%20people%2C%20architectural%20beauty&width=800&height=500&seq=footer1&orientation=landscape"
                alt="レディースキッド店内"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Right copy */}
            <div>
              <p
                className="text-[#B8895A] text-xs tracking-widest mb-6 uppercase"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                Brand Philosophy
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold text-white leading-snug mb-6"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                おしゃれを、
                <br />
                我慢と引き換えにしない。
              </h2>
              <p
                className="text-white/60 text-sm leading-loose"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                私たちは、
                <br />
                <span className="text-white/80">"楽な靴を履く"</span>ではなく、
                <br />
                <span className="text-[#E8C99A] font-medium">"好きな靴を、無理なく履ける"</span>
                <br />
                を大切にしています。
              </p>

              {/* Newsletter */}
              <div className="mt-8">
                <p className="text-white/50 text-xs mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  足と靴の最新情報をお届けします
                </p>
                {status === "success" ? (
                  <div className="flex items-center gap-2 text-[#E8C99A] text-sm py-3">
                    <i className="ri-checkbox-circle-line" />
                    <span style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>ご登録ありがとうございます！</span>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="flex gap-2"
                  >
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="メールアドレスを入力"
                      required
                      className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/30 px-4 py-2.5 rounded-full text-sm focus:outline-none focus:border-[#E8C99A]/60 min-w-0"
                      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                    />
                    <button
                      type="submit"
                      className="bg-[#E8C99A] text-[#1C1917] px-5 py-2.5 rounded-full text-xs font-bold whitespace-nowrap hover:bg-[#D4A574] transition-colors cursor-pointer"
                      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                    >
                      登録
                    </button>
                  </form>
                )}
                {status === "error" && (
                  <p className="text-red-400 text-xs mt-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    エラーが発生しました。再度お試しください。
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer links */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center bg-white rounded-full">
                <span className="text-[#1C1917] text-xs font-bold">LK</span>
              </div>
              <span
                className="text-white text-sm font-semibold tracking-widest"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                レディースキッド
              </span>
            </div>
            <p className="text-white/40 text-xs leading-loose mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              30〜40代女性のための
              <br />
              足と靴のフィッティング専門店
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: "ri-line-fill", label: "LINE" },
                { icon: "ri-instagram-line", label: "Instagram" },
                { icon: "ri-youtube-line", label: "YouTube" },
              ].map((s) => (
                <a
                  key={s.label}
                  href="#"
                  className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full text-white/60 hover:bg-white/20 hover:text-white transition-all cursor-pointer"
                  aria-label={s.label}
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Nav groups */}
          {navGroups.map((group) => (
            <div key={group.title}>
              <h3
                className="text-white text-sm font-semibold mb-4"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {group.title}
              </h3>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      className="text-white/40 text-xs hover:text-white/80 transition-colors cursor-pointer"
                      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-xs" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            © 2025 レディースキッド All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            {["プライバシーポリシー", "利用規約", "採用情報"].map((item) => (
              <a
                key={item}
                href="#"
                className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}